'use client'

import { memo } from 'react'
import { useTranslations } from 'next-intl'
import {
  cartPricesSelector,
  cartRouteFeeAmountSelector,
  cartRouteInfoSelector,
  cartShippingMethodInfoAmountSelector,
  cartTaxesTotalAmountSelector,
  Price,
  storeConfigSelector,
} from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'

import YotpoCart from '@/components/yotpoWidget/YotpoCart'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui'

const CheckoutTotal = () => {
  const cartPrices = useAppSelector(cartPricesSelector)
  const cartShippingMethodInfoAmount = useAppSelector(cartShippingMethodInfoAmountSelector)
  const taxesTotalAmount = useAppSelector(cartTaxesTotalAmountSelector)
  const routeInfo = useAppSelector(cartRouteInfoSelector)
  const cartRouteFeeAmount = useAppSelector(cartRouteFeeAmountSelector)
  const storeConfig = useAppSelector(storeConfigSelector)
  const t = useTranslations('Cart')

  return (
    <div className="mt-[32px] space-y-[24px] text-[#444446]">
      <div className="flex justify-between">
        <span>{t('subtotal')}</span>
        <span>
          {!storeConfig?.tax_cart_display?.subtotal_include_tax &&
            cartPrices?.subtotal_excluding_tax && (
              <Price price={cartPrices?.subtotal_excluding_tax} />
            )}
          {storeConfig?.tax_cart_display?.subtotal_include_tax &&
            cartPrices?.subtotal_including_tax && (
              <Price price={cartPrices?.subtotal_including_tax} />
            )}
        </span>
      </div>

      {routeInfo?.show_route && routeInfo.is_insured && cartRouteFeeAmount && (
        <li className="flex items-center justify-between">
          <span>{cartRouteFeeAmount.label}</span>
          <Price
            containerStyle="text-gray-8 font-miSansMedium380 text-base"
            price={cartRouteFeeAmount.amount}
          />
        </li>
      )}

      {!!cartPrices?.discounts?.length && (
        <li className="flex justify-between">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="hover:none pb-0 pt-0">
                <div className="flex flex-1 items-center justify-between pr-1">
                  <span className="text-left text-base text-gray-6">Discount:</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="mt-3">
                <ul>
                  {cartPrices.discounts.map((discount, index) => {
                    return (
                      <li
                        key={`${discount?.label}-${index}`}
                        className="flex items-center justify-between">
                        <span className="text-gray-6">{discount?.label}</span>
                        <span className="inline-flex items-center">
                          <span className="text-[#da291c]">-</span>
                          <Price
                            containerStyle="text-[#da291c] font-miSansMedium380 text-base ml-1"
                            price={discount?.amount}
                          />
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </li>
      )}

      {cartShippingMethodInfoAmount && (
        <li className="flex justify-between gap-4">
          <span>{cartShippingMethodInfoAmount.label}</span>
          <Price
            containerStyle="text-gray-8 font-miSansMedium380 text-base"
            price={cartShippingMethodInfoAmount.amount}
          />
        </li>
      )}

      {storeConfig?.tax_cart_display?.full_summary && !!cartPrices?.applied_taxes?.length && (
        <li className="flex justify-between">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="hover:none pb-0 pt-0">
                <div className="flex flex-1 items-center justify-between pr-1">
                  <span className="text-left text-base text-gray-6">Tax:</span>
                  <span className="text-right text-gray-8">
                    <Price
                      containerStyle="text-gray-8 font-miSansMedium380 text-base"
                      price={{ value: taxesTotalAmount }}
                    />
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="mt-3">
                <ul>
                  {cartPrices.applied_taxes.map((tax, index) => {
                    return (
                      <li
                        key={`${tax?.label}-${index}`}
                        className="flex items-center justify-between">
                        <span className="text-gray-6">{tax?.label}</span>
                        <span>
                          <Price
                            containerStyle="text-gray-8 font-miSansMedium380 text-base"
                            price={tax?.amount}
                          />
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </li>
      )}
      <div className="flex justify-between border-t border-[#F3F3F4] pt-[30px]">
        <span className="text-[#000]">{t('total')}</span>
        <span className="text-[18px] text-[#DA291C]">
          <Price
            color="primary"
            currencyStyle="text-2xl"
            textStyle="text-2xl"
            fractionStyle="text-2xl"
            price={{ value: cartPrices?.grand_total?.value }}
          />
        </span>
      </div>
      <div>
        <YotpoCart />
      </div>
    </div>
  )
}

export default memo(CheckoutTotal)
